import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getMyConsultations,
  requestConsultation,
} from '../../services/accountApi';
import { friendlyError } from '../../utils/errors';
import { trackConsultationRequest } from '../../utils/analytics';

const fmt = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

export default function AccountConsultations() {
  const { user, profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    notes: '',
  });

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await getMyConsultations(user.id);
    setRows(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!profile?.phone) {
      return setError(
        'Please add your phone number in My Details first — the astrologer needs it to reach you.'
      );
    }

    setBusy(true);
    const { error } = await requestConsultation(user.id, {
      customerName: profile?.full_name || user.email,
      customerPhone: profile.phone,
      customerEmail: user.email,
      birthDate: form.birthDate || profile?.birth_date || null,
      birthTime: form.birthTime,
      birthPlace: form.birthPlace,
      notes: form.notes,
    });
    setBusy(false);

    if (error) return setError(friendlyError(error));

    trackConsultationRequest(form.birthPlace ? 'with_birth_details' : 'basic');

    setSuccess(
      'Request received. Our astrologer will contact you on WhatsApp shortly.'
    );
    setForm({ birthDate: '', birthTime: '', birthPlace: '', notes: '' });
    setShowForm(false);
    load();
  };

  if (loading) return <div className="account-loading">Loading…</div>;

  return (
    <div>
      <div className="account-section-head">
        <h3 className="account-h3">Consultations</h3>
        <button
          className="account-btn-sm"
          onClick={() => {
            setShowForm((s) => !s);
            setSuccess('');
          }}
        >
          {showForm ? 'Cancel' : '+ Request consultation'}
        </button>
      </div>

      {success && <p className="account-success">{success}</p>}

      {showForm && (
        <form className="account-form boxed" onSubmit={handleSubmit}>
          <p className="field-hint">
            Share your birth details and our certified Vedic astrologer will
            recommend the right gemstone for you.
          </p>

          <div className="form-two">
            <label>
              Date of Birth
              <input
                type="date"
                value={form.birthDate || profile?.birth_date || ''}
                onChange={(e) => set('birthDate', e.target.value)}
              />
            </label>
            <label>
              Time of Birth
              <input
                type="time"
                value={form.birthTime}
                onChange={(e) => set('birthTime', e.target.value)}
              />
            </label>
          </div>

          <label>
            Place of Birth
            <input
              value={form.birthPlace}
              onChange={(e) => set('birthPlace', e.target.value)}
              placeholder="City, State"
            />
          </label>

          <label>
            What would you like guidance on?
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Career, health, relationships…"
            />
          </label>

          {error && <p className="account-error">{error}</p>}

          <button className="account-btn" disabled={busy}>
            {busy ? 'Sending…' : 'Request Consultation'}
          </button>
        </form>
      )}

      {rows.length === 0 ? (
        <div className="account-empty">
          <p>No consultations yet.</p>
          <p className="mini-sub">
            Request one above to get personalised gemstone guidance.
          </p>
        </div>
      ) : (
        <div className="consult-list">
          {rows.map((c) => (
            <div key={c.id} className="consult-card">
              <div className="consult-top">
                <span className={`chip chip-${c.status}`}>{c.status}</span>
                <span className="mini-sub">
                  Requested {fmt(c.created_at) || '—'}
                </span>
              </div>

              {c.astrologer_name && (
                <p>
                  <strong>Astrologer:</strong> {c.astrologer_name}
                </p>
              )}
              {c.scheduled_at && (
                <p>
                  <strong>Scheduled:</strong> {fmt(c.scheduled_at)}
                </p>
              )}
              {(c.birth_date || c.birth_time || c.birth_place) && (
                <p className="mini-sub">
                  {[c.birth_date, c.birth_time, c.birth_place]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
              {c.notes && <p className="consult-notes">{c.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
